<template>
  <div>
    <b-row class="align-items-start mt-sm-0 mt-n1">
      <b-col
        cols="12"
        order-sm="1"
        order="2"
        sm="6"
      >
        <div class="text-md text-thick">
          Audio - {{ name }}
        </div>
      </b-col>
      <b-col
        class="d-flex justify-content-end"
        cols="12"
        order-sm="2"
        order="1"
        sm="6"
      >
        <b-button
          variant="icon-light"
          @click="$emit('remove')"
        >
          <GjIcon name="Delete" />
          Remove Audio
        </b-button>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <validation-provider
          #default="{ errors }"
          name="Audio Codec"
          class="w-100"
        >
          <input-field
            name="preset_audio-codec"
            label="Audio Codec"
            label-position="top"
            :error="errors[0]"
            :info="audioInfo.audioCodec"
            info-position="label"
            @openInfo="openInfo"
          >
            <custom-select
              v-if="isEditable"
              id="preset_audio-codec"
              v-model="audioDescription.codecType"
              :options="filteredAudioCodecOptions()"
            />
            <div v-else>
              {{
                getText(
                  audioDescription.codecTypeOptions,
                  audioDescription.codecType,
                )
              }}
            </div>
          </input-field>
        </validation-provider>
      </b-col>
    </b-row>
    <b-row>
      <b-col
        cols="12"
        sm="6"
      >
        <validation-provider
          #default="{ errors }"
          name="Profile"
          class="w-100"
        >
          <input-field
            name="preset_audio-codec_profile"
            label="Profile"
            label-position="top"
            :error="errors[0]"
          >
            <custom-select
              v-if="isEditable"
              id="codec_profile"
              v-model="audioDescription.codecProfile"
              :options="getOptions(audioDescription.codecProfileOptions)"
            />
            <div v-else>
              {{
                getText(
                  audioDescription.codecProfileOptions,
                  audioDescription.codecProfile,
                )
              }}
            </div>
          </input-field>
        </validation-provider>
      </b-col>
      <b-col
        cols="12"
        sm="6"
      >
        <validation-provider
          #default="{ errors }"
          name="Bitrate Control Mode"
          class="w-100"
        >
          <input-field
            name="preset_audio-bitrate_control_mode"
            label="Bitrate Control mode"
            label-position="top"
            :error="errors[0]"
            :info="audioInfo.bitrateControlMode"
            info-position="label"
            @openInfo="openInfo"
          >
            <custom-select
              v-if="isEditable"
              id="preset_audio-bitrate_control_mode"
              v-model="audioDescription.rateControlMode"
              :options="getOptions(audioDescription.rateControlModeOptions)"
            />
            <div v-else>
              {{
                getText(
                  audioDescription.rateControlModeOptions,
                  audioDescription.rateControlMode,
                )
              }}
            </div>
          </input-field>
        </validation-provider>
      </b-col>
    </b-row>
    <b-row>
      <b-col
        cols="12"
        sm="6"
      >
        <validation-provider
          #default="{ errors }"
          name="Coding Mode"
          class="w-100"
        >
          <input-field
            name="preset_audio-coding_mode"
            label="Coding mode"
            label-position="top"
            :error="errors[0]"
            :info="audioInfo.codingMode"
            info-position="label"
            @openInfo="openInfo"
          >
            <custom-select
              v-if="isEditable"
              id="coding_mode"
              v-model="audioDescription.codingMode"
              :options="getOptions(audioDescription.codingModeOptions)"
            />
            <div v-else>
              {{
                getText(
                  audioDescription.codingModeOptions,
                  audioDescription.codingMode,
                )
              }}
            </div>
          </input-field>
        </validation-provider>
      </b-col>
      <b-col />
    </b-row>
    <b-row>
      <b-col
        cols="12"
        sm="6"
      >
        <validation-provider
          #default="{ errors }"
          name="Sample Rate"
          rules="required|min_value:1000"
          class="w-100"
        >
          <input-field
            name="preset_audio-sample_rate"
            label="Sample rate (kHz)*"
            label-position="top"
            :error="errors[0] ? 'The Sample rate field is required' : null"
            :info="audioInfo.sampleRate"
            info-position="label"
            @openInfo="openInfo"
          >
            <custom-select
              v-if="isEditable"
              id="preset_audio-bitrate"
              v-model="audioDescription.sampleRate"
              :options="sampleRateOptions"
            />
            <div v-else>
              {{ audioDescription.sampleRate }}
            </div>
          </input-field>
        </validation-provider>
      </b-col>
      <b-col
        cols="12"
        sm="6"
      >
        <validation-provider
          #default="{ errors }"
          name="Bitrate"
          rules="required|min_value:1000"
          class="w-100"
        >
          <input-field
            name="preset_audio-bitrate"
            label="Bitrate (kbit/s)*"
            label-position="top"
            :error="errors[0] ? 'The Bitrate field is required' : null"
            :info="audioInfo.bitrate"
            info-position="label"
            @openInfo="openInfo"
          >
            <custom-select
              v-if="isEditable"
              id="preset_audio-bitrate"
              v-model="audioDescription.bitrate"
              :options="bitrateOptions"
            />
            <div v-else>
              {{ audioDescription.bitrate }}
            </div>
          </input-field>
        </validation-provider>
      </b-col>
    </b-row>
    <b-row class="border-default border-t mb-2" />
    <b-row>
      <b-col
        cols="12"
        sm="6"
      >
        <validation-provider
          #default="{ errors }"
          name="Specification"
          class="w-100"
        >
          <input-field
            name="preset_audio-specification"
            label="Specification"
            label-position="top"
            :error="errors[0]"
            :info="audioInfo.specification"
            info-position="label"
            @openInfo="openInfo"
          >
            <custom-select
              v-if="isEditable"
              id="preset_audio-specification"
              v-model="audioDescription.specification"
              :options="filteredSpecifications()"
            />
            <div v-else>
              {{
                getText(
                  audioDescription.specificationOptions,
                  audioDescription.specification,
                )
              }}
            </div>
          </input-field>
        </validation-provider>
        <validation-provider
          #default="{ errors }"
          name="Audio Description Broadcaster Mix"
          class="w-100"
        >
          <input-field
            name="preset_audio-audio_description_broadcaster_mix"
            label="Audio Description Broadcaster Mix"
            label-position="top"
            :error="errors[0]"
            :info="audioInfo.broadcasterMix"
            info-position="label"
            @openInfo="openInfo"
          >
            <custom-select
              v-if="isEditable"
              id="preset_audio-audio_description_broadcaster_mix"
              v-model="audioDescription.audioDescriptionBroadcasterMix"
              :options="
                getOptions(
                  audioDescription.audioDescriptionBroadcasterMixOptions,
                )
              "
            />
            <div v-else>
              {{
                getText(
                  audioDescription.audioDescriptionBroadcasterMixOptions,
                  audioDescription.audioDescriptionBroadcasterMix,
                )
              }}
            </div>
          </input-field>
        </validation-provider>
      </b-col>
      <b-col
        cols="12"
        sm="6"
      >
        <validation-provider
          #default="{ errors }"
          name="Sample Rate"
          class="w-100"
        >
          <input-field
            name="preset_audio-raw_format"
            label="Raw Format"
            label-position="top"
            :error="errors[0]"
          >
            <div class="w-100">
              <custom-select
                v-if="isEditable"
                id="preset_audio-raw_format"
                v-model="audioDescription.rawFormat"
                :options="getOptions(audioDescription.rawFormatOptions)"
              />
              <div v-else>
                {{
                  getText(
                    audioDescription.rawFormatOptions,
                    audioDescription.rawFormat,
                  )
                }}
              </div>
              <custom-alert
                items-center
                class="mt-1"
                icon-name="Info"
              >
                {{ audioInfo.rawFormat.message }}
              </custom-alert>
            </div>
          </input-field>
        </validation-provider>
      </b-col>
    </b-row>
    <b-row>
      <b-col
        cols="12"
        sm="6"
      >
        <input-field
          class="mb-3"
          name="preset_video-transcribe"
          label="Transcribe"
          label-position="right"
          info-position="label"
          :info="audioInfo.transcribe"
          @openInfo="openInfo"
        >
          <b-form-checkbox
            id="preset_video-transcribe"
            v-model="audioDescription.transcribe"
            name="check-button"
            switch
            stacked
          />
        </input-field>
      </b-col>
      <b-col />
    </b-row>
  </div>
</template>

<script>
import { ValidationProvider } from 'vee-validate'
import { required } from '@validations'
import helpers from '@/mixins/helpers'

import InputField from '@/components/form/InputField.vue'

import audioInfo from './audioInfo'

export default {
  name: 'AudioSettings',
  components: {
    ValidationProvider,
    InputField,
  },
  mixins: [helpers],
  props: {
    isEditable: {
      type: Boolean,
      default: true,
    },
    edit: {
      type: Boolean,
      default: false,
    },
    outputPreset: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      required,
      audioInfo,
      audioDescription: null,
    }
  },
  computed: {
    audioDescriptionSampleRate() {
      return this.audioDescription ? this.audioDescription.sampleRate : null
    },
    name() {
      return this.audioDescription.codecTypeOptions.find(
        cto => cto.id === this.audioDescription.codecType,
      ).value
    },
    sampleRateOptions() {
      return this.audioDescription.audioSampleBitrateOptions
        ? Object.keys(
          this.audioDescription.audioSampleBitrateOptions,
        ).map(opt => ({ value: parseFloat(opt) * 1000, text: opt }))
        : []
    },
    bitrateOptions() {
      if (this.audioDescription.audioSampleBitrateOptions) {
        const selectedSROption = this.sampleRateOptions.find(
          sro => sro.value === this.audioDescription.sampleRate,
        )
        if (selectedSROption) {
          return this.audioDescription.audioSampleBitrateOptions[
            selectedSROption.text
          ].map(opt => ({ value: parseFloat(opt) * 1000, text: opt }))
        }
      }
      return []
    },
  },
  watch: {
    audioDescriptionSampleRate(val) {
      if (val) {
        if (this.bitrateOptions.length) {
          this.audioDescription.bitrate = this.bitrateOptions[0].value
        }
        if (this.$route.params.cloned || this.edit) {
          this.audioDescription.bitrate = this.$store.state.outputPresets.outputPreset?.audioDescription?.bitrate
        }
      }
    },
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      const emptyModel = {
        ...this.$store.state.outputPresets.emptyOutputPreset.audioDescription,
      }
      if (this.edit) {
        this.audioDescription = {
          ...(this.$store.state.outputPresets.outputPreset?.audioDescription
          || emptyModel),
        }
      } else {
        this.audioDescription = { ...emptyModel }
        this.audioDescription.sampleRate = parseFloat(this.audioDescription.sampleRate) * 1000
      }
    },
    filteredAudioCodecOptions() {
      const { codecTypeOptions } = this.audioDescription

      if (this.outputPreset.container.type === 0 || this.outputPreset.container.type === 3) {
        const filteredOptions = this.getOptions(codecTypeOptions.filter(x => x.id === 0))
        if (!filteredOptions.map(x => x.value).includes(this.audioDescription.codecType)) {
          this.audioDescription.codecType = 0
        }
        return filteredOptions
      }

      if (this.outputPreset.container.type === 1) {
        const filteredOptions = this.getOptions(codecTypeOptions.filter(x => x.id === 0 || x.id === 1))
        if (!filteredOptions.map(x => x.value).includes(this.audioDescription.codecType)) {
          this.audioDescription.codecType = 0
        }
        return filteredOptions
      }

      return this.getOptions(codecTypeOptions)
    },
    filteredSpecifications() {
      const { specificationOptions } = this.audioDescription

      if (this.audioDescription.codecType === 2 && this.outputPreset.container.type === 2) {
        this.audioDescription.specification = 2
        return this.getOptions(specificationOptions.filter(x => x.id === 2))
      }

      if (this.outputPreset.container.type === 0 || this.outputPreset.container.type === 3) {
        this.audioDescription.specification = 1
        return this.getOptions(specificationOptions.filter(x => x.id === 1))
      }

      if ((this.audioDescription.codecType === 0 || this.audioDescription.codecType === 1) && this.outputPreset.container.type === 1) {
        this.audioDescription.specification = 0
      }

      return this.outputPreset.container.type === 1
        ? this.getOptions(specificationOptions.filter(x => x.id === 0))
        : this.getOptions(specificationOptions)
    },
    setAudioCodec(codecType) {
      this.audioDescription.codecType = codecType
    },
    setSpecification(specification) {
      this.audioDescription.specification = specification
    },
    openInfo(e) {
      this.$emit('openInfo', e)
    },
  },
}
</script>
